import React from "react";
import clsx from "clsx";
import { useSelector } from "react-redux";
import Tooltip from "antd/lib/tooltip";
import { PartitionType } from "Models/partition";
import { SML_COLORS, TIMELINE_MESSAGES_LIMIT } from "Components/constants";
import Gate from "./Gate";
import Indicator from "./Indicator";
import Line from "./Line";
import Log from "./Log";
import Head from "./Head";
import { getPartition, getLeadConsumers, getLog } from "Redux/selectors";
import { groupToLetter } from "./utils";
import styles from "./Partition.module.css";

type PartitionProps = {
    logUid: string;
};

const Partition = ({ logUid }: PartitionProps) => {
    const partition = useSelector(getPartition(logUid));

    const partitionOffsets = React.useMemo(
        () => Object.entries(partition.offset),
        [partition.offset]
    );

    const groupsPerOffset = React.useMemo(
        () =>
            partitionOffsets.reduce<{ [index: number]: string[] }>(
                (acc, [group, offset]) => ({
                    ...acc,
                    [offset]: [...(acc[offset] || []), group],
                }),
                {}
            ),
        [partitionOffsets]
    );

    const log = useSelector((state) => getLog(state, logUid));
    const getOffsetMessageIndex = React.useCallback(
        (messageId: number) => log.findIndex((m) => m.id === messageId),
        [log]
    );

    const leadConsumers = useSelector(getLeadConsumers(partition?.id));

    return (
        <Tooltip
            title={`${
                partition.type === PartitionType.LEAD ? "Leader" : "Replica"
            } partition`}
            placement="topLeft"
        >
            <div className={clsx(styles.root)}>
                <Line
                    direction="horizontal"
                    length={(TIMELINE_MESSAGES_LIMIT + 0.5) * 60}
                    className={styles.line}
                />
                <Head
                    text={`${partition.id}`}
                    color={SML_COLORS[partition.id % SML_COLORS.length]}
                    replica={partition.type === PartitionType.REPLICA}
                />
                <Log
                    logUid={logUid}
                    replica={partition.type === PartitionType.REPLICA}
                    delay={partition.type === PartitionType.REPLICA ? 0.25 : 0}
                >
                    {partition.type === PartitionType.LEAD &&
                        leadConsumers.map((consumer, idx) => (
                            <Gate
                                key={idx}
                                position={getOffsetMessageIndex(
                                    consumer.offsets[partition.id]
                                )}
                                processedId={
                                    consumer.recentMessage?.partitionId ===
                                    partition.id
                                        ? consumer.recentMessage.id
                                        : undefined
                                }
                                delay={0.75}
                            />
                        ))}
                    {partition.type === PartitionType.LEAD &&
                        partitionOffsets.map(([group, offset]) => {
                            if (offset <= 0) return null;
                            const groupsOnOffset = groupsPerOffset[offset];
                            const groupIndex = groupsOnOffset.indexOf(group);
                            const shift =
                                ((groupsOnOffset.length - 1) / 2 - groupIndex) *
                                0.2;

                            const consumer = leadConsumers.find(
                                (c) =>
                                    c.offsets[partition.id] !== undefined &&
                                    c.group === group
                            );

                            const delay = 0.5 + (consumer?.interval || 1);

                            return (
                                <Indicator
                                    key={group}
                                    text={groupToLetter(parseInt(group))}
                                    color={
                                        SML_COLORS[
                                            partition.id % SML_COLORS.length
                                        ]
                                    }
                                    position={
                                        getOffsetMessageIndex(offset) + shift
                                    }
                                    delay={delay}
                                    partition={partition.id}
                                    offset={offset}
                                    group={group}
                                    inverse
                                    flipped
                                    type="target"
                                />
                            );
                        })}
                </Log>
            </div>
        </Tooltip>
    );
};

export default Partition;
